/**
 * Widget i18n — language-agnostic.
 *
 * Translation DATA is delivered per-tenant in the bootstrap response
 * (`WidgetConfig.i18n`, for the configured locale ONLY) — source of truth is
 * apps/api `lang/{locale}/widget.php`. The shipped bundle contains ZERO
 * translation strings; adding a language = add a server lang file (no rebuild).
 *
 * Per-language *logic* is delegated to the browser `Intl` API (plural selection,
 * relative-time wording) so the code stays locale-agnostic.
 *
 * Locale resolution (drives Intl + which server dict the bootstrap returns):
 *   explicit `locale` attr → bootstrap locale → <html lang> → navigator → 'pl'.
 */

export type WidgetLocale = string;

/** Plain translation data sent from the server (no functions). */
export interface WidgetI18n {
  openChat: string;
  /** Search-box element defaults (bootstrap-served; optional — older payloads may lack them). */
  searchPlaceholder?: string;
  searchButton?: string;
  closeChat: string;
  newConversation: string;
  newConversationConfirm: string;
  newConversationTitle: string;
  newConversationConfirmBtn: string;
  newConversationCancel: string;
  peekGreetingFallback: string;
  /** template with {name} */
  peekStartChat: string;
  /** aria-label of the nudge bubble's close (X) button */
  nudgeDismiss: string;
  typing: string;
  active: string;
  responding: string;
  inputPlaceholder: string;
  islandPlaceholder: string;
  send: string;
  genericError: string;
  errorStatusPrefix: string;
  /** CLDR plural-category → template with {count} */
  cartCounter: Record<string, string>;
  cartSubmitButton: string;
  cartBarTitle: string;
  /** external_cart mode: shop-cart op chips + the shop-cart link bar. */
  externalCartPending: string;
  externalCartAdded: string;
  externalCartRemoved: string;
  externalCartCleared: string;
  externalCartRejected: string;
  externalCartFailed: string;
  externalCartViewTitle: string;
  externalCartViewPending: string;
  externalCartViewEmpty: string;
  externalCartViewUnavailable: string;
  externalCartViewFailed: string;
  externalCartRedirecting: string;
  openShopCart: string;
  cartTitle: string;
  cartQtyPieces: string;
  cartTotalLabel: string;
  orderTotalLabel: string;
  cartBack: string;
  orderFormTitle: string;
  orderFormNameLabel: string;
  orderFormPhoneLabel: string;
  phoneOtherCountry: string;
  orderFormEmailLabel: string;
  orderFormEmailRequired: string;
  orderFormNotesLabel: string;
  orderFormSubmit: string;
  orderFormErrorName: string;
  orderFormErrorPhone: string;
  orderFormErrorEmail: string;
  checkPrice: string;
  productDetails: string;
  addToCart: string;
  addToCartShort: string;
  variantPrompt: string;
  variantAxisLabels: Record<string, string>;
  escalationFormTitle: string;
  escalationFormSubtitle: string;
  escalationNameLabel: string;
  escalationEmailLabel: string;
  escalationPhoneLabel: string;
  escalationPhoneOptional: string;
  escalationMessageLabel: string;
  escalationSubmit: string;
  escalationErrorRequired: string;
  escalationErrorPhone: string;
  escalationSubmittedConfirmation: string;
  contactFormTitle: string;
  contactFormSubtitle: string;
  contactSubmitButton: string;
  contactSubmittedConfirmation: string;
  cookieBannerTitle: string;
  cookieReopenLink: string;
  cookieReopenLinkTerms: string;
  cookieClose: string;
  cookieCookiesHeading: string;
  cookieCookiesBody: string;
  cookieAdminHeading: string;
  cookieAdminBody: string;
  cookieAiHeading: string;
  cookieAiBody: string;
  poweredBy: string;
  /** word for the <45s bucket ("just now" / "przed chwilą") */
  timeAgoNow: string;
}

/**
 * Minimal fallback used ONLY on the bootstrap-FAILURE render path (no server i18n
 * available). Real values for the few keys that path touches (launcher + error
 * banner); '' elsewhere. The component renders `nothing` until bootstrap resolves,
 * so on the success path this is never read. Keeps the bundle free of full
 * dictionaries while guaranteeing no undefined deref + back-compat with an older API.
 */
export const FALLBACK_I18N: WidgetI18n = {
  openChat: 'Otwórz chat',
  closeChat: 'Zamknij',
  genericError: 'Coś poszło nie tak. Spróbuj ponownie.',
  errorStatusPrefix: 'Błąd',
  newConversation: '',
  newConversationConfirm: '',
  newConversationTitle: '',
  newConversationConfirmBtn: '',
  newConversationCancel: '',
  peekGreetingFallback: '',
  peekStartChat: '',
  nudgeDismiss: 'Zamknij',
  typing: '',
  active: '',
  responding: '',
  inputPlaceholder: '',
  islandPlaceholder: '',
  send: '',
  cartCounter: { other: '' },
  cartSubmitButton: '',
  cartBarTitle: '',
  externalCartPending: '',
  externalCartAdded: '',
  externalCartRemoved: '',
  externalCartCleared: '',
  externalCartRejected: '',
  externalCartFailed: '',
  externalCartViewTitle: '',
  externalCartViewPending: '',
  externalCartViewEmpty: '',
  externalCartViewUnavailable: '',
  externalCartViewFailed: '',
  externalCartRedirecting: '',
  openShopCart: '',
  cartTitle: '',
  cartQtyPieces: '',
  cartTotalLabel: '',
  orderTotalLabel: '',
  cartBack: '',
  orderFormTitle: '',
  orderFormNameLabel: '',
  orderFormPhoneLabel: '',
  phoneOtherCountry: '',
  orderFormEmailLabel: '',
  orderFormEmailRequired: '',
  orderFormNotesLabel: '',
  orderFormSubmit: '',
  orderFormErrorName: '',
  orderFormErrorPhone: '',
  orderFormErrorEmail: '',
  checkPrice: '',
  productDetails: '',
  addToCart: '',
  addToCartShort: '',
  variantPrompt: '',
  variantAxisLabels: {},
  escalationFormTitle: '',
  escalationFormSubtitle: '',
  escalationNameLabel: '',
  escalationEmailLabel: '',
  escalationPhoneLabel: '',
  escalationPhoneOptional: '',
  escalationMessageLabel: '',
  escalationSubmit: '',
  escalationErrorRequired: '',
  escalationErrorPhone: '',
  escalationSubmittedConfirmation: '',
  contactFormTitle: '',
  contactFormSubtitle: '',
  contactSubmitButton: '',
  contactSubmittedConfirmation: '',
  cookieBannerTitle: '',
  cookieReopenLink: '',
  cookieReopenLinkTerms: '',
  cookieClose: '',
  cookieCookiesHeading: '',
  cookieCookiesBody: '',
  cookieAdminHeading: '',
  cookieAdminBody: '',
  cookieAiHeading: '',
  cookieAiBody: '',
  poweredBy: '',
  timeAgoNow: 'przed chwilą',
};

/** Replace {placeholders} in a template with the given vars. */
export function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

/** Count message via CLDR plural category (Intl.PluralRules) → per-category template. */
export function formatCartCounter(locale: string, count: number, forms: Record<string, string>): string {
  let category = 'other';
  try {
    category = new Intl.PluralRules(locale).select(count);
  } catch {
    /* unknown locale → other */
  }
  return interpolate(forms[category] ?? forms.other ?? '', { count });
}

/**
 * Relative "X ago" wording. Bucketing thresholds (the product UX) stay in code +
 * locale-agnostic; the WORDING (incl. plural forms) comes from Intl.RelativeTimeFormat.
 * The <45s bucket uses the data `timeAgoNow` word (Intl's "now"/"teraz" differs).
 */
export function formatRelativeTime(locale: string, deltaSeconds: number, nowWord: string): string {
  const s = Math.max(0, deltaSeconds);
  if (s < 45) return nowWord;
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  } catch {
    rtf = new Intl.RelativeTimeFormat('en', { numeric: 'always' });
  }
  const min = Math.floor(s / 60);
  if (min < 60) return rtf.format(-Math.max(1, min), 'minute');
  const h = Math.floor(s / 3600);
  if (h < 24) return rtf.format(-h, 'hour');
  return rtf.format(-Math.floor(s / 86400), 'day');
}

/**
 * Resolve the widget locale: explicit override (attr or bootstrap) → <html lang>
 * → navigator → 'pl'. Returns the 2-letter prefix; the supported set lives
 * server-side (Intl tolerates any locale, and the bootstrap decides the dict).
 */
export function resolveLocale(override?: string | null): WidgetLocale {
  const candidates: (string | undefined | null)[] = [
    override,
    typeof document !== 'undefined' ? document.documentElement.lang : undefined,
    typeof navigator !== 'undefined' ? navigator.language : undefined,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c !== '') return c.slice(0, 2).toLowerCase();
  }
  return 'pl';
}
